import { CalendarDays, Clock3, IdCard, Pencil, UserRound } from 'lucide-react'
import type { ReactNode } from 'react'
import { useTranslation } from 'react-i18next'
import { Link } from 'react-router'
import { Avatar } from '@/components/ui/avatar'
import { Button } from '@/components/ui/button'
import { Card } from '@/components/ui/card'
import { fmtDate } from '@/features/shared/format'
import { cn } from '@/lib/utils'
import type { Personel } from '../types'
import { PartnerTags } from './PartnerTags'
import { PersonelRowActions } from './PersonelRowActions'
import { PersonelStatusPill } from './StatusPill'

function Meta({ icon, label, children, className }: { icon: ReactNode; label: string; children: ReactNode; className?: string }) {
  return (
    <div className={cn('flex min-w-0 items-center gap-2.5', className)}>
      <span className="flex size-8 shrink-0 items-center justify-center rounded-lg bg-muted text-muted-foreground [&_svg]:size-4">
        {icon}
      </span>
      <div className="min-w-0">
        <div className="text-[11px] font-medium tracking-wide text-muted-foreground uppercase">{label}</div>
        <div className="truncate text-sm font-semibold">{children}</div>
      </div>
    </div>
  )
}

export function ProfileCard({
  personel,
  kidem,
  manager,
  onEdit,
  children,
}: {
  personel: Personel
  kidem?: string
  manager?: Pick<Personel, 'id' | 'pozisyon'>
  onEdit?: () => void
  children?: ReactNode
}) {
  const { t } = useTranslation()
  return (
    <Card className="overflow-hidden rounded-2xl border-border/70 shadow-sm">
      <div className="flex flex-col gap-4 p-4 sm:flex-row sm:items-start sm:gap-5 sm:p-5 lg:p-6">
        <Avatar name={personel.ad} className="size-12 rounded-xl sm:size-20 sm:rounded-2xl lg:size-24 [&>span]:bg-primary/10 [&>span]:text-base [&>span]:text-primary sm:[&>span]:text-xl lg:[&>span]:text-2xl" />
        <div className="min-w-0 flex-1 space-y-5">
          <div className="flex flex-wrap items-start justify-between gap-3">
            <div className="min-w-0 space-y-1.5">
              <div className="flex flex-wrap items-center gap-2.5">
                <h2 className="text-xl font-semibold tracking-tight lg:text-2xl">{personel.ad}</h2>
                <PersonelStatusPill durum={personel.durum} />
                <PartnerTags personel={personel} />
              </div>
              <p className="text-sm text-muted-foreground">
                {personel.pozisyon} · {personel.departman}
              </p>
            </div>
            <div className="flex items-center gap-2">
              {onEdit ? (
                <Button
                  variant="outline"
                  size="sm"
                  className="border-primary/40 bg-card text-primary shadow-xs hover:bg-primary/5 hover:text-primary"
                  onClick={onEdit}
                >
                  <Pencil />
                  {t('hr.personel.detail.duzenle')}
                </Button>
              ) : null}
              <PersonelRowActions personel={personel} variant="profile" />
            </div>
          </div>

          <div className="grid grid-cols-2 gap-4 xl:flex xl:flex-wrap xl:gap-x-10">
            <Meta icon={<IdCard />} label={t('hr.personel.fields.sicil')}>
              {personel.sicil}
            </Meta>
            <Meta icon={<CalendarDays />} label={t('hr.personel.detail.iseGiris')}>
              {fmtDate(personel.ise_giris)}
            </Meta>
            {kidem ? (
              <Meta icon={<Clock3 />} label={t('hr.personel.fields.kidem')}>
                {kidem}
              </Meta>
            ) : null}
            <Meta icon={<UserRound />} label={t('hr.personel.fields.yonetici')} className={kidem ? undefined : 'col-span-2 sm:col-span-1'}>
              {manager ? (
                <Link to={`/hr/personel/${manager.id}`} className="text-primary hover:underline">
                  {personel.yonetici}
                </Link>
              ) : (
                personel.yonetici
              )}
              {manager ? <span className="ml-1 font-normal text-muted-foreground">· {manager.pozisyon}</span> : null}
            </Meta>
          </div>
        </div>
      </div>
      {children ? <div className="border-t border-border/70 bg-muted/20 px-3 py-2.5 sm:px-5">{children}</div> : null}
    </Card>
  )
}
